import L from 'leaflet';
import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import { Link } from 'react-router-dom';

import TextLogo from '../../img/brand/fulllogo_nobuffer.png';
import { slugify } from '../../utils/slugify';
import './maps.css';

let DefaultIcon = L.icon({
  iconUrl:
    'https://api.golflists.com/storage/v1/object/public/map_icons/icons/basePin.png?t=2024-01-26T16%3A42%3A44.686Z',
  iconSize: [24, 24],
});
L.Marker.prototype.options.icon = DefaultIcon;

function findMedianValue(values) {
  const validValues = values.filter((val) => val != null).sort((a, b) => a - b);
  const mid = Math.floor(validValues.length / 2);

  return validValues.length % 2 !== 0
    ? validValues[mid]
    : (validValues[mid - 1] + validValues[mid]) / 2;
}

function CountyCourseMap({ courses }) {
  const [map, setMap] = useState(null);

  const { validCourses, medianLatitude, medianLongitude } = useMemo(() => {
    const filteredCourses = courses.filter(
      (course) => course.latitude != null && course.longitude != null,
    );

    const latitudes = filteredCourses.map((course) => course.latitude);
    const longitudes = filteredCourses.map((course) => course.longitude);

    return {
      validCourses: filteredCourses,
      medianLatitude: findMedianValue(latitudes),
      medianLongitude: findMedianValue(longitudes),
    };
  }, [courses]);

  useEffect(() => {
    if (map && validCourses.length > 0) {
      map.setView([medianLatitude, medianLongitude], map.getZoom());
    }
  }, [map, validCourses, medianLatitude, medianLongitude]);

  if (!validCourses || validCourses.length === 0) {
    return <div>No course data</div>;
  }

  const markers = validCourses.map((course, index) => {
    const iconUrl =
      course.county_rank < 6
        ? 'https://api.golflists.com/storage/v1/object/public/map_icons/icons/starPin.png?t=2024-01-26T13%3A02%3A23.433Z'
        : 'https://api.golflists.com/storage/v1/object/public/map_icons/icons/basePin.png?t=2024-01-26T16%3A42%3A44.686Z';

    const iconSize = course.county_rank < 6 ? [24, 24] : [14, 14];

    const customIcon = L.icon({
      iconUrl,
      iconSize,
    });

    return (
      <Marker
        key={index}
        position={[course.latitude, course.longitude]}
        icon={customIcon}
      >
        <Popup>
          <Link to={`/lists/golf-courses/${slugify(course.course_name)}`}>
            {course.course_name}
          </Link>{' '}
          - #{course.county_rank}
        </Popup>
      </Marker>
    );
  });

  return (
    <div className="max-h-[400px] lg:max-h-[500px] single-leaflet-container bg-white p-1">
      <div id="course-map" className="relative w-full h-full">
        <div className="absolute bottom-0 left-0 right-0 bg-white z-40">
          <h3 className="w-full px-5 mx-auto text-center text-base sm:text-lg font-semibold left-0 right-0">
            {courses[0].course_county} Golf Course Map
          </h3>
          <p className="w-full px-5 mx-auto text-center text-gray-700 text-xs italic left-0 right-0 ">
            Shows all golf courses in {courses[0].course_county}, with stars for
            the top ranked 5.
          </p>
        </div>
        <MapContainer
          center={[medianLatitude, medianLongitude]}
          zoom={9}
          scrollWheelZoom={true}
          style={{ width: '100%', height: '100%' }}
          whenCreated={setMap}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {markers}
        </MapContainer>
        <img
          loading="lazy"
          src={TextLogo}
          alt="Logo"
          className="absolute top-2 left-1/2 transform -translate-x-1/2 w-52 sm:w-56 border border-indigo-500 rounded-md px-4 py-2 bg-white z-30"
        />
      </div>
    </div>
  );
}

export default CountyCourseMap;
